import { useState, useEffect, useMemo } from 'react';
import { AlertTriangle, Eye, Trash2, Printer, CheckCircle } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { usePermissions } from '../hooks/usePermissions';
import { Location } from '../types';
import Modal from './Modal';
import StockChangeModal from './StockChangeModal';
import { SkeletonTable } from './Skeleton';
import { EmptyStateNoData, EmptyStateError } from './EmptyState';
import { formatDate, formatDateTime, getMadridDate, toDateInputValue } from '../utils/dateFormatter';
import SearchableSelect from './SearchableSelect';

// Función helper para obtener el color del stock según el mínimo
const getStockColor = (stock: number, minStock: number): string => {
  if (stock < minStock) {
    return 'var(--error)'; // Rojo: por debajo del mínimo
  } else if (stock <= minStock * 1.2) {
    return 'var(--warning)'; // Amarillo: igual o hasta 20% por encima del mínimo
  } else {
    return 'var(--success)'; // Verde: más de 20% por encima del mínimo
  }
};

// Función helper para verificar si mostrar icono de alerta (stock < 50% del mínimo)
const shouldShowAlert = (stock: number, minStock: number): boolean => {
  return stock < minStock * 0.5;
};

interface Need {
  metal_piece_id: number;
  cod_pieza: string;
  nombre_pieza: string;
  current_stock_listo: number;
  current_stock_acabado: number;
  current_stock_en_proceso: number;
  pending_coating: number;
  min_stock_acabado: number;
  needed: number;
}

interface CoatingOrder {
  id: number;
  code: string;
  proveedor: string;
  tipo: 'PVD' | 'DLC';
  status: 'PENDING' | 'RECEIVED' | 'CANCELLED';
  created_at: string;
  expected_at?: string;
  lines: Array<{
    id: number;
    metal_piece_id: number;
    qty_ordered: number;
    qty_received: number;
    metal_piece: {
      id: number;
      cod_pieza: string;
      nombre_pieza: string;
      material_base: 'PLATA' | 'LATON';
    };
  }>;
}

export default function CoatingOrders() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const { canEdit } = usePermissions();
  const [orders, setOrders] = useState<CoatingOrder[]>([]);
  const [needs, setNeeds] = useState<Need[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<CoatingOrder | null>(null);
  const [selectedLines, setSelectedLines] = useState<Map<number, number>>(new Map());
  const [formData, setFormData] = useState({ proveedor: '', tipo: 'PVD' as 'PVD' | 'DLC', expected_at: '' });
  const [lineQtyInputs, setLineQtyInputs] = useState<Map<number, string>>(new Map());
  const [filterTipo, setFilterTipo] = useState<'PVD' | 'DLC' | 'ALL'>('ALL');
  const [manuallyAddedPieces, setManuallyAddedPieces] = useState<Set<number>>(new Set());
  const [allMetalPieces, setAllMetalPieces] = useState<Array<{ id: number; cod_pieza: string; nombre_pieza: string; material_base: 'PLATA' | 'LATON' }>>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isStockChangeModalOpen, setIsStockChangeModalOpen] = useState(false);
  const [stockChanges, setStockChanges] = useState<Array<{ item_code: string; item_name: string; location_name: string; stock_before: number; stock_after: number; quantity_changed: number }>>([]);
  const [isReceivingAll, setIsReceivingAll] = useState(false);

  // Filtrar necesidades: mostrar solo las que tienen necesidad O las agregadas manualmente
  const visibleNeeds = useMemo(() => {
    return needs.filter(need => {
      const hasNeed = need.needed > 0;
      const isManuallyAdded = manuallyAddedPieces.has(need.metal_piece_id);
      return hasNeed || isManuallyAdded;
    });
  }, [needs, manuallyAddedPieces]);

  const selectableNeedIds = visibleNeeds
    .filter((need) => Math.floor(need.current_stock_listo) > 0)
    .map((need) => need.metal_piece_id);
  const allNeedsSelected =
    selectableNeedIds.length > 0 && selectableNeedIds.every((id) => selectedLines.has(id));

  useEffect(() => {
    loadOrders();
  }, [filterTipo]);

  useEffect(() => {
    if (formData.tipo) {
      loadNeeds(formData.tipo);
    }
  }, [formData.tipo]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = filterTipo !== 'ALL' ? { tipo: filterTipo } : {};
      const response = await api.get('/coating-orders', { params });
      setOrders(response.data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Error al cargar pedidos';
      showError(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadNeeds = async (tipo: 'PVD' | 'DLC'): Promise<Need[]> => {
    try {
      const response = await api.get('/coating-orders/needs', { params: { tipo } });
      setNeeds(response.data);
      return response.data;
    } catch (err: any) {
      console.error('Error al cargar necesidades:', err);
      return [];
    }
  };

  const loadAllMetalPieces = async () => {
    try {
      const response = await api.get('/metal-pieces');
      // Solo piezas de latón pueden tener revestimiento
      const latonPieces = response.data.filter((p: any) => p.material_base === 'LATON');
      setAllMetalPieces(latonPieces.map((p: any) => ({
        id: p.id,
        cod_pieza: p.cod_pieza,
        nombre_pieza: p.nombre_pieza,
        material_base: p.material_base,
      })));
    } catch (err: any) {
      console.error('Error al cargar piezas metálicas:', err);
    }
  };

  const handleManualAddPiece = async (piece: { id: number; cod_pieza: string; nombre_pieza: string; material_base: 'PLATA' | 'LATON' }) => {
    // Verificar si la pieza ya está en needs
    const existingNeed = needs.find(n => n.metal_piece_id === piece.id);
    if (existingNeed) {
      // Si ya existe, solo marcarla como agregada manualmente
      setManuallyAddedPieces(prev => new Set([...prev, piece.id]));
      // Seleccionar con cantidad sugerida
      const available = getAvailableQty(existingNeed);
      const suggested = available > 0 ? Math.min(available, Math.round(existingNeed.needed)) : 1;
      const newMap = new Map(selectedLines);
      newMap.set(piece.id, suggested);
      setSelectedLines(newMap);
    } else {
      // Si no existe, consultar stock real del backend
      try {
        // Cargar ubicaciones si no están cargadas
        let allLocations = locations;
        if (allLocations.length === 0) {
          const locationsResponse = await api.get('/locations');
          allLocations = locationsResponse.data;
          setLocations(allLocations);
        }

        // Obtener el tipo de revestimiento del formulario
        const tipo = formData.tipo; // 'PVD' o 'DLC'
        const listoLocation = allLocations.find((l: Location) => l.code === `LISTO_${tipo}`);
        const procesoLocation = allLocations.find((l: Location) => l.code === `EN_PROCESO_${tipo}`);
        const acabadoLocation = allLocations.find((l: Location) => l.code === `ACABADO_${tipo}`);

        if (!listoLocation || !acabadoLocation || !procesoLocation) {
          throw new Error('Ubicaciones no encontradas');
        }

        // Consultar stock real para cada ubicación
        const [stockListoResponse, stockAcabadoResponse, stockProcesoResponse, minStockResponse, multiplierResponse] = await Promise.all([
          api.get('/stock', { params: { item_type: 'METAL', item_id: piece.id, location_id: listoLocation.id } }),
          api.get('/stock', { params: { item_type: 'METAL', item_id: piece.id, location_id: acabadoLocation.id } }),
          api.get('/stock', { params: { item_type: 'METAL', item_id: piece.id, location_id: procesoLocation.id } }),
          api.get('/min-stock', { params: { item_type: 'METAL', item_id: piece.id, location_id: acabadoLocation.id } }),
          api.get('/settings/min-stock-multiplier')
        ]);

        const stockListoData = stockListoResponse.data;
        const stockAcabadoData = stockAcabadoResponse.data;
        const stockProcesoData = stockProcesoResponse.data;
        const minStockData = minStockResponse.data;
        const multiplier = parseFloat(multiplierResponse.data.value || '1');

        const currentStockListo = Array.isArray(stockListoData) && stockListoData.length > 0
          ? parseFloat(stockListoData[0].qty) || 0
          : 0;
        const currentStockAcabado = Array.isArray(stockAcabadoData) && stockAcabadoData.length > 0
          ? parseFloat(stockAcabadoData[0].qty) || 0
          : 0;
        const currentStockProceso = Array.isArray(stockProcesoData) && stockProcesoData.length > 0
          ? parseFloat(stockProcesoData[0].qty) || 0
          : 0;

        const minStockBase = Array.isArray(minStockData) && minStockData.length > 0
          ? parseFloat(minStockData[0].min_qty_base) || 0
          : 0;
        const effectiveMin = Math.round(minStockBase * multiplier);

        // Crear necesidad con valores reales
        // Si hay stock en LISTO, se puede mandar todo el stock disponible
        const needed = currentStockListo > 0 ? Math.floor(currentStockListo) : 10;

        const newNeed: Need = {
          metal_piece_id: piece.id,
          cod_pieza: piece.cod_pieza,
          nombre_pieza: piece.nombre_pieza,
          current_stock_listo: currentStockListo,
          current_stock_acabado: currentStockAcabado,
          current_stock_en_proceso: currentStockProceso,
          pending_coating: 0,
          min_stock_acabado: effectiveMin,
          needed: needed,
        };
        setNeeds(prev => [...prev, newNeed]);
        setManuallyAddedPieces(prev => new Set([...prev, piece.id]));
        const newMap = new Map(selectedLines);
        newMap.set(piece.id, needed);
        setSelectedLines(newMap);
      } catch (err: any) {
        console.error('Error al cargar stock para pieza manual:', err);
        // Fallback: crear necesidad con valores por defecto
        const newNeed: Need = {
          metal_piece_id: piece.id,
          cod_pieza: piece.cod_pieza,
          nombre_pieza: piece.nombre_pieza,
          current_stock_listo: 0,
          current_stock_acabado: 0,
          current_stock_en_proceso: 0,
          pending_coating: 0,
          min_stock_acabado: 0,
          needed: 10,
        };
        setNeeds(prev => [...prev, newNeed]);
        setManuallyAddedPieces(prev => new Set([...prev, piece.id]));
        const newMap = new Map(selectedLines);
        newMap.set(piece.id, 10);
        setSelectedLines(newMap);
      }
    }
    showSuccess(`${piece.cod_pieza} agregado manualmente`);
  };

  const handleOpenCreateModal = async () => {
    const defaultTipo: 'PVD' | 'DLC' = 'PVD';
    setSelectedLines(new Map());
    setManuallyAddedPieces(new Set());

    // Pre-llenar proveedor con "Oerlikon"
    // Pre-llenar fecha esperada con 15 días desde hoy (en zona horaria de Madrid)
    const today = getMadridDate();
    const expectedDate = new Date(today);
    expectedDate.setDate(today.getDate() + 15);
    const expectedDateStr = toDateInputValue(expectedDate);
    setFormData({ proveedor: 'Oerlikon', tipo: defaultTipo, expected_at: expectedDateStr });
    await Promise.all([loadNeeds(defaultTipo), loadAllMetalPieces()]);
    setIsCreateModalOpen(true);
  };

  const getAvailableQty = (need: Need): number =>
    Math.max(0, Math.floor(need.current_stock_listo));

  const handleToggleLine = (need: Need) => {
    const available = getAvailableQty(need);
    if (available <= 0) {
      return;
    }
    const newMap = new Map(selectedLines);
    if (newMap.has(need.metal_piece_id)) {
      newMap.delete(need.metal_piece_id);
    } else {
      const suggested = Math.max(1, Math.min(available, Math.round(need.needed)));
      newMap.set(need.metal_piece_id, suggested);
    }
    setSelectedLines(newMap);
  };

  const toggleSelectAllNeeds = () => {
    const newMap = new Map(selectedLines);
    if (allNeedsSelected) {
      selectableNeedIds.forEach((id) => newMap.delete(id));
    } else {
      selectableNeedIds.forEach((id) => {
        const need = needs.find((n) => n.metal_piece_id === id);
        if (!need) return;
        const available = getAvailableQty(need);
        if (available <= 0) return;
        const suggested = Math.max(1, Math.min(available, Math.round(need.needed)));
        newMap.set(id, suggested);
      });
    }
    setSelectedLines(newMap);
  };

  const handleUpdateQty = (metalPieceId: number, qty: number) => {
    const newMap = new Map(selectedLines);
    const need = needs.find((n) => n.metal_piece_id === metalPieceId);
    if (!need) {
      newMap.delete(metalPieceId);
      setSelectedLines(newMap);
      return;
    }
    const available = getAvailableQty(need);
    if (available <= 0) {
      newMap.delete(metalPieceId);
      setSelectedLines(newMap);
      return;
    }
    const clampedQty = Math.min(available, Math.max(0, qty));
    if (clampedQty > 0) {
      newMap.set(metalPieceId, clampedQty);
    } else {
      newMap.delete(metalPieceId);
    }
    setSelectedLines(newMap);
  };

  const handleCreateOrder = async () => {
    if (selectedLines.size === 0) {
      showError('Debes seleccionar al menos una línea');
      return;
    }
    if (!formData.proveedor.trim()) {
      showError('El nombre del proveedor es obligatorio');
      return;
    }

    try {
      const lines = Array.from(selectedLines.entries()).map(([metal_piece_id, qty_ordered]) => ({
        metal_piece_id,
        qty_ordered,
      }));

      await api.post('/coating-orders', {
        proveedor: formData.proveedor,
        tipo: formData.tipo,
        expected_at: formData.expected_at || undefined,
        lines,
      });
      showSuccess('Pedido de revestimiento creado correctamente');
      setIsCreateModalOpen(false);
      setSelectedLines(new Map());
      loadOrders();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al crear pedido');
    }
  };

  const handleOpenDetailModal = async (order: CoatingOrder) => {
    try {
      const response = await api.get(`/coating-orders/${order.id}`);
      const detailedOrder = response.data;
      const qtyInputs = new Map<number, string>();
      detailedOrder.lines.forEach((line: any) => {
        const pending = line.qty_ordered - line.qty_received;
        if (pending > 0) {
          qtyInputs.set(line.id, pending.toString());
        }
      });
      setLineQtyInputs(qtyInputs);
      setSelectedOrder(detailedOrder);
      setIsDetailModalOpen(true);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar detalles');
    }
  };

  const handleReceiveLine = async (orderId: number, lineId: number, qty: number) => {
    if (qty <= 0) {
      showError('La cantidad debe ser mayor a 0');
      return;
    }

    const response = await api.post(`/coating-orders/${orderId}/lines/${lineId}/receive`, {
      qty_received: qty,
    });

    if (response.data.receiveInfo?.stockChanges && response.data.receiveInfo.stockChanges.length > 0) {
      setStockChanges(response.data.receiveInfo.stockChanges);
      setIsStockChangeModalOpen(true);
    }

    return response.data;
  };

  const handleReceiveAllLines = async () => {
    if (!selectedOrder || selectedOrder.status === 'RECEIVED' || isReceivingAll) return;
    try {
      setIsReceivingAll(true);
      const linesToReceive = selectedOrder.lines
        .filter(l => l.qty_ordered - l.qty_received > 0)
        .map(l => ({
          lineId: l.id,
          qty_received: l.qty_ordered - l.qty_received
        }));

      if (linesToReceive.length === 0) {
        showSuccess('No hay líneas pendientes de recibir');
        setIsReceivingAll(false);
        return;
      }

      const response = await api.post(`/coating-orders/${selectedOrder.id}/lines/receive`, {
        lines: linesToReceive
      });

      const results = response.data.results || [];
      const hasPartialReceives = results.some((r: any) => r.message);
      const allZero = results.every((r: any) => r.qty_received === 0);

      if (allZero) {
        showError('No se pudo recibir ninguna línea por falta de stock en proceso/listo');
      } else if (hasPartialReceives) {
        showSuccess('Recepción completada parcialmente según stock disponible');
      } else {
        showSuccess('Todas las líneas han sido recibidas correctamente');
      }

      if (response.data.stockChanges && response.data.stockChanges.length > 0) {
        setStockChanges(response.data.stockChanges);
        setIsStockChangeModalOpen(true);
      }

      const refreshed = await api.get(`/coating-orders/${selectedOrder.id}`);
      setSelectedOrder(refreshed.data);
      loadOrders();

      // Cerrar el modal después de recibir todo
      setIsDetailModalOpen(false);
      setSelectedOrder(null);
    } catch (err: any) {
      console.error('Error al recibir líneas:', err);
      showError(err.response?.data?.error || 'Error al recibir todas las líneas');
    } finally {
      setIsReceivingAll(false);
    }
  };
  const handleReceiveLineMessage = (message?: string) => {
    if (message) {
      showSuccess(message);
    } else {
      showSuccess('Línea recibida correctamente');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este pedido?')) {
      return;
    }

    try {
      await api.delete(`/coating-orders/${id}`);
      showSuccess('Pedido eliminado correctamente');
      loadOrders();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al eliminar pedido');
    }
  };

  const handleCloseOrder = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres cerrar manualmente este pedido? Lo que no se haya recibido se devolverá a stock LISTO.')) {
      return;
    }

    try {
      await api.post(`/coating-orders/${id}/close`);
      showSuccess('Pedido cerrado correctamente');
      loadOrders();
      if (formData.tipo) {
        loadNeeds(formData.tipo);
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cerrar pedido');
    }
  };

  const handlePrintPDF = async (orderId: number) => {
    try {
      const response = await api.get(`/coating-orders/${orderId}/pdf`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Crear un iframe temporal para imprimir
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = url;

      document.body.appendChild(iframe);

      iframe.onload = () => {
        setTimeout(() => {
          try {
            iframe.contentWindow?.print();
            setTimeout(() => {
              document.body.removeChild(iframe);
              window.URL.revokeObjectURL(url);
            }, 1000);
          } catch (error) {
            // Fallback si falla el iframe
            const printWindow = window.open(url, '_blank');
            if (printWindow) {
              setTimeout(() => {
                printWindow.print();
              }, 500);
            }
            document.body.removeChild(iframe);
          }
        }, 500);
      };
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al generar PDF');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'var(--warning)';
      case 'RECEIVED':
        return 'var(--success)';
      case 'CANCELLED':
        return 'var(--error)';
      default:
        return 'var(--text-secondary)';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Pendiente';
      case 'RECEIVED':
        return 'Recibido';
      case 'CANCELLED':
        return 'Cancelado';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="tab-content">
        <SkeletonTable rows={8} columns={7} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-content">
        <EmptyStateError message={error} onRetry={loadOrders} />
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="flex-between mb-2">
        <h2>Pedidos de Revestimiento</h2>
        <div className="flex gap-2">
          <select
            className="input"
            value={filterTipo}
            onChange={(e) => setFilterTipo(e.target.value as 'PVD' | 'DLC' | 'ALL')}
            style={{ width: '150px' }}
          >
            <option value="ALL">Todos</option>
            <option value="PVD">PVD</option>
            <option value="DLC">DLC</option>
          </select>
          {canEdit('revestimiento') && (
            <button className="btn btn-primary" onClick={handleOpenCreateModal}>
              + Nuevo Pedido
            </button>
          )}
        </div>
      </div>


      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Código</th>
              <th>Proveedor</th>
              <th>Tipo</th>
              <th>Estado</th>
              <th>Líneas</th>
              <th>Fecha Creación</th>
              <th>Fecha Esperada</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center p-0">
                  <EmptyStateNoData onCreate={canEdit('revestimiento') ? handleOpenCreateModal : undefined} />
                </td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr key={order.id}>
                  <td><strong>{order.code}</strong></td>
                  <td>{order.proveedor}</td>
                  <td>
                    <span
                      style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: order.tipo === 'PVD' ? 'rgba(255, 149, 0, 0.1)' : 'rgba(0, 113, 227, 0.1)',
                        color: order.tipo === 'PVD' ? 'var(--warning)' : 'var(--accent)',
                      }}
                    >
                      {order.tipo}
                    </span>
                  </td>
                  <td>
                    <span style={{ color: getStatusColor(order.status) }}>
                      {getStatusLabel(order.status)}
                    </span>
                  </td>
                  <td>
                    {order.lines.length} línea(s)
                    <br />
                    <small style={{ color: 'var(--text-secondary)' }}>
                      {order.lines.reduce((sum, l) => sum + l.qty_ordered, 0)} unidades
                    </small>
                  </td>
                  <td>{formatDate(order.created_at)}</td>
                  <td>
                    {order.expected_at
                      ? formatDate(order.expected_at)
                      : '-'}
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleOpenDetailModal(order)}
                        title="Ver"
                        style={{
                          padding: '0.5rem',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--text-secondary)',
                          transition: 'all 0.2s ease',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                          e.currentTarget.style.color = 'var(--text-primary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = 'var(--text-secondary)';
                        }}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => handlePrintPDF(order.id)}
                        title="Imprimir"
                        style={{
                          padding: '0.5rem',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--text-secondary)',
                          transition: 'all 0.2s ease',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                          e.currentTarget.style.color = 'var(--text-primary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = 'var(--text-secondary)';
                        }}
                      >
                        <Printer size={16} />
                      </button>
                      {canEdit('revestimiento') && order.status === 'PENDING' && (
                        <button
                          onClick={() => handleCloseOrder(order.id)}
                          title="Cerrar Manualmente"
                          style={{
                            padding: '0.5rem',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--success)',
                            transition: 'all 0.2s ease',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                            e.currentTarget.style.color = 'var(--success)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'var(--success)';
                          }}
                        >
                          <CheckCircle size={16} />
                        </button>
                      )}
                      {canEdit('revestimiento') && (
                        <button
                          onClick={() => handleDelete(order.id)}
                          title="Eliminar"
                          style={{
                            padding: '0.5rem',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--text-secondary)',
                            transition: 'all 0.2s ease',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                            e.currentTarget.style.color = 'var(--text-primary)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'var(--text-secondary)';
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Crear */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Nuevo Pedido de Revestimiento"
        size="large"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setIsCreateModalOpen(false)}>
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleCreateOrder}
              disabled={selectedLines.size === 0 || !formData.proveedor.trim()}
              data-modal-default="true"
            >
              Crear Pedido
            </button>
          </>
        }
      >
        <div className="input-group">
          <label className="input-label">Proveedor *</label>
          <input
            type="text"
            className={`input ${!formData.proveedor.trim() ? 'input-error' : ''}`}
            value={formData.proveedor}
            onChange={(e) => setFormData({ ...formData, proveedor: e.target.value })}
            required
            placeholder="Nombre del proveedor"
          />
        </div>

        <div className="input-group">
          <label className="input-label">Tipo *</label>
          <select
            className="input"
            value={formData.tipo}
            onChange={(e) => {
              setFormData({ ...formData, tipo: e.target.value as 'PVD' | 'DLC' });
              setSelectedLines(new Map());
            }}
            required
          >
            <option value="PVD">PVD</option>
            <option value="DLC">DLC</option>
          </select>
        </div>

        <div className="input-group">
          <label className="input-label">Fecha Esperada</label>
          <input
            type="date"
            className="input"
            value={formData.expected_at}
            onChange={(e) => setFormData({ ...formData, expected_at: e.target.value })}
          />
        </div>

        <h3 style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>Líneas del Pedido</h3>

        <div style={{ marginBottom: '1.5rem' }}>
          <SearchableSelect<{ id: number; cod_pieza: string; nombre_pieza: string; material_base: 'PLATA' | 'LATON' }>
            items={allMetalPieces}
            onSelect={handleManualAddPiece}
            getItemLabel={(item) => item.nombre_pieza}
            getItemId={(item) => item.id}
            getItemCode={(item) => item.cod_pieza}
            placeholder="Buscar pieza por código o nombre para agregar manualmente..."
            label="Agregar Pieza Manualmente"
            excludeIds={new Set([...selectedLines.keys()])}
          />
        </div>

        {visibleNeeds.length === 0 ? (
          <div className="table-container">
            <EmptyStateNoData />
          </div>
        ) : (
          <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: '50px', position: 'sticky', top: 0, backgroundColor: 'var(--bg-secondary)', zIndex: 1 }}>
                    <input
                      type="checkbox"
                      checked={allNeedsSelected}
                      disabled={selectableNeedIds.length === 0}
                      onChange={toggleSelectAllNeeds}
                    />
                  </th>
                  <th style={{ minWidth: '180px', position: 'sticky', top: 0, backgroundColor: 'var(--bg-secondary)', zIndex: 1 }}>Código</th>
                  <th style={{ minWidth: '300px', position: 'sticky', top: 0, backgroundColor: 'var(--bg-secondary)', zIndex: 1 }}>Nombre</th>
                  <th style={{ minWidth: '120px', textAlign: 'center', position: 'sticky', top: 0, backgroundColor: 'var(--bg-secondary)', zIndex: 1 }}>Stock Origen<br /><small>(Listo {formData.tipo})</small></th>
                  <th style={{ minWidth: '140px', textAlign: 'center', position: 'sticky', top: 0, backgroundColor: 'var(--bg-secondary)', zIndex: 1 }}>Stock En Proceso<br /><small>(Proveedor)</small></th>
                  <th style={{ minWidth: '120px', textAlign: 'center', position: 'sticky', top: 0, backgroundColor: 'var(--bg-secondary)', zIndex: 1 }}>Pendiente</th>
                  <th style={{ minWidth: '120px', textAlign: 'center', position: 'sticky', top: 0, backgroundColor: 'var(--bg-secondary)', zIndex: 1 }}>Stock Destino<br /><small>(Acabado {formData.tipo})</small></th>
                  <th style={{ minWidth: '100px', textAlign: 'center', position: 'sticky', top: 0, backgroundColor: 'var(--bg-secondary)', zIndex: 1 }}>Mínimo</th>
                  <th style={{ minWidth: '120px', textAlign: 'center', position: 'sticky', top: 0, backgroundColor: 'var(--bg-secondary)', zIndex: 1 }}>Necesario</th>
                  <th style={{ minWidth: '150px', position: 'sticky', top: 0, backgroundColor: 'var(--bg-secondary)', zIndex: 1 }}>Cantidad</th>
                </tr>
              </thead>
              <tbody>
                {visibleNeeds.map((need) => {
                  const availableQty = getAvailableQty(need);
                  const isSelectable = availableQty > 0;
                  const selectedValue = selectedLines.get(need.metal_piece_id);
                  const qty = isSelectable ? (selectedValue ?? '') : ''; // Show empty if not selected
                  const isSelected = selectedLines.has(need.metal_piece_id);
                  return (
                    <tr key={need.metal_piece_id} style={{ opacity: isSelectable ? 1 : 0.5 }}>
                      <td>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={!isSelectable}
                          onChange={() => handleToggleLine(need)}
                        />
                      </td>
                      <td><strong>{need.cod_pieza}</strong></td>
                      <td>{need.nombre_pieza}</td>
                      <td style={{ textAlign: 'center' }}>
                        <strong style={{ color: getStockColor(need.current_stock_listo, need.min_stock_acabado) }}>
                          {need.current_stock_listo}
                        </strong>
                        {shouldShowAlert(need.current_stock_listo, need.min_stock_acabado) && (
                          <span style={{ marginLeft: '0.25rem', fontSize: '1rem' }} title="Stock crítico: menos del 50% del mínimo">
                            <AlertTriangle size={16} strokeWidth={1.5} />
                          </span>
                        )}
                        <br />
                        <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                          → {Math.max(0, need.current_stock_listo - (selectedLines.get(need.metal_piece_id) || 0))}
                        </small>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <strong style={{ color: getStockColor(need.current_stock_en_proceso, need.min_stock_acabado) }}>
                          {need.current_stock_en_proceso}
                        </strong>
                        {shouldShowAlert(need.current_stock_en_proceso, need.min_stock_acabado) && (
                          <span style={{ marginLeft: '0.25rem', fontSize: '1rem' }} title="Stock en proceso por debajo de la mitad del mínimo">
                            <AlertTriangle size={16} strokeWidth={1.5} />
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center', color: 'var(--accent)', fontWeight: 'bold' }}>
                        {need.pending_coating || 0}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {(() => {
                          const stockDespues = need.current_stock_acabado + (selectedLines.get(need.metal_piece_id) || 0);
                          return (
                            <>
                              <strong style={{ color: getStockColor(stockDespues, need.min_stock_acabado) }}>
                                {stockDespues}
                              </strong>
                              {shouldShowAlert(stockDespues, need.min_stock_acabado) && (
                                <span style={{ marginLeft: '0.25rem', fontSize: '1rem' }} title="Stock crítico: menos del 50% del mínimo">
                                  <AlertTriangle size={16} strokeWidth={1.5} />
                                </span>
                              )}
                              <br />
                              <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                                (actual: <span style={{ color: getStockColor(need.current_stock_acabado, need.min_stock_acabado) }}>
                                  {need.current_stock_acabado}
                                  {shouldShowAlert(need.current_stock_acabado, need.min_stock_acabado) && (
                                    <span style={{ marginLeft: '0.25rem' }} title="Stock crítico: menos del 50% del mínimo">
                                      <AlertTriangle size={16} strokeWidth={1.5} />
                                    </span>
                                  )}
                                </span>)
                              </small>
                            </>
                          );
                        })()}
                      </td>
                      <td style={{ textAlign: 'center' }}>{need.min_stock_acabado}</td>
                      <td style={{ textAlign: 'center' }}>
                        <strong style={{ color: 'var(--error)' }}>{need.needed}</strong>
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          max={availableQty || undefined}
                          className="input"
                          value={qty}
                          onChange={(e) => {
                            const val = e.target.value;
                            const parsed = val === '' ? 0 : parseInt(val, 10);
                            if (!isNaN(parsed)) {
                              handleUpdateQty(need.metal_piece_id, parsed);
                            }
                          }}
                          disabled={!isSelectable}
                          placeholder={isSelectable ? "0" : "-"}
                          style={{ width: '120px', minWidth: '120px' }}
                        />
                        <small style={{ color: 'var(--text-secondary)' }}>
                          Máx: {availableQty}
                        </small>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {selectedLines.size > 0 && (
          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
            <strong>Total seleccionado: {selectedLines.size} línea(s)</strong>
            <br />
            <small style={{ color: 'var(--text-secondary)' }}>
              {Array.from(selectedLines.values()).reduce((sum, qty) => sum + qty, 0)} unidades
            </small>
          </div>
        )}
      </Modal>

      {/* Modal Detalle */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedOrder(null);
        }}
        title={selectedOrder ? `Pedido: ${selectedOrder.code}` : 'Detalle'}
        size="large"
        footer={
          <div className="flex gap-2" style={{ justifyContent: 'flex-end', width: '100%' }}>
            {selectedOrder && selectedOrder.status !== 'RECEIVED' && (
              <button
                className="btn btn-success"
                onClick={handleReceiveAllLines}
                disabled={isReceivingAll}
              >
                {isReceivingAll ? 'Recibiendo...' : 'Recibir todo'}
              </button>
            )}
            {selectedOrder && selectedOrder.status === 'PENDING' && (
              <button
                className="btn btn-error"
                style={{ backgroundColor: 'var(--error)', color: 'white' }}
                onClick={() => {
                  handleCloseOrder(selectedOrder.id);
                  setIsDetailModalOpen(false);
                  setSelectedOrder(null);
                }}
              >
                Cerrar Orden
              </button>
            )}
            <button
              className="btn btn-secondary"
              onClick={() => {
                setIsDetailModalOpen(false);
                setSelectedOrder(null);
              }}
            >
              Cerrar
            </button>
          </div>
        }
      >
        {selectedOrder && (
          <div>
            <div style={{ marginBottom: '1.5rem' }}>
              <p><strong>Proveedor:</strong> {selectedOrder.proveedor}</p>
              <p>
                <strong>Tipo:</strong>{' '}
                <span
                  style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    backgroundColor: selectedOrder.tipo === 'PVD' ? 'rgba(255, 149, 0, 0.1)' : 'rgba(0, 113, 227, 0.1)',
                    color: selectedOrder.tipo === 'PVD' ? 'var(--warning)' : 'var(--accent)',
                  }}
                >
                  {selectedOrder.tipo}
                </span>
              </p>
              <p>
                <strong>Estado:</strong>{' '}
                <span style={{ color: getStatusColor(selectedOrder.status) }}>
                  {getStatusLabel(selectedOrder.status)}
                </span>
              </p>
              <p>
                <strong>Fecha Creación:</strong>{' '}
                {formatDateTime(selectedOrder.created_at)}
              </p>
              {selectedOrder.expected_at && (
                <p>
                  <strong>Fecha Esperada:</strong>{' '}
                  {formatDateTime(selectedOrder.expected_at)}
                </p>
              )}
            </div>

            <h3 style={{ marginBottom: '1rem' }}>Líneas</h3>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Pieza</th>
                    <th>Pedido</th>
                    <th>Recibido</th>
                    <th>Pendiente</th>
                    <th>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.lines.map((line) => {
                    const pending = line.qty_ordered - line.qty_received;
                    const qtyInput = lineQtyInputs.get(line.id) || '';
                    return (
                      <tr key={line.id}>
                        <td>
                          {line.metal_piece.cod_pieza}
                          <br />
                          <small style={{ color: 'var(--text-secondary)' }}>
                            {line.metal_piece.nombre_pieza}
                          </small>
                        </td>
                        <td>{line.qty_ordered}</td>
                        <td>
                          <strong style={{ color: 'var(--success)' }}>{line.qty_received}</strong>
                        </td>
                        <td>
                          <strong style={{ color: pending > 0 ? 'var(--warning)' : 'var(--success)' }}>
                            {pending}
                          </strong>
                        </td>
                        <td>
                          {pending > 0 && selectedOrder.status !== 'RECEIVED' ? (
                            <div className="flex gap-1" style={{ alignItems: 'center' }}>
                              <input
                                type="number"
                                min="1"
                                max={pending}
                                className="input"
                                value={qtyInput}
                                onChange={(e) => {
                                  const newMap = new Map(lineQtyInputs);
                                  newMap.set(line.id, e.target.value);
                                  setLineQtyInputs(newMap);
                                }}
                                placeholder="Cantidad"
                                style={{ width: '80px' }}
                              />
                              <button
                                className="btn btn-primary"
                                onClick={async () => {
                                  const qty = parseInt(qtyInput, 10);
                                  if (qty > 0 && qty <= pending) {
                                    try {
                                      const response = await handleReceiveLine(selectedOrder.id, line.id, qty);
                                      handleReceiveLineMessage(response?.receiveInfo?.message);
                                      const newMap = new Map(lineQtyInputs);
                                      newMap.delete(line.id);
                                      setLineQtyInputs(newMap);
                                      const refreshed = await api.get(`/coating-orders/${selectedOrder.id}`);
                                      setSelectedOrder(refreshed.data);
                                      loadOrders();
                                    } catch (err: any) {
                                      showError(err.response?.data?.error || 'Error al recibir línea');
                                    }
                                  }
                                }}
                                style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                              >
                                Recibir
                              </button>
                            </div>
                          ) : (
                            <span style={{ color: 'var(--text-secondary)' }}>Completo</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>

      <StockChangeModal
        isOpen={isStockChangeModalOpen}
        onClose={() => setIsStockChangeModalOpen(false)}
        title="Cambios de Stock - Pedido de Revestimiento Recibido"
        changes={stockChanges}
      />
    </div>
  );
}
